import LazyVideo from "@components/LandingPageComponents/LazyVideoComponent";
import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import CTAButtons from "@components/new-ui/CTAButtons";
import CTASection from "@components/new-ui/CTASection";
import { FAQSectionProps } from "@components/new-ui/FAQSection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import KeyFeaturesSection from "@components/new-ui/KeyFeaturesSection";
import NewLayout from "@components/new-ui/Layout";
import ProblemStatementSection from "@components/new-ui/SpotlightSection";
import TestimonialSection from "@components/new-ui/TestimonialSection";
import "keen-slider/keen-slider.min.css";
import Head from "next/head";
import Image from "next/image";
import React from "react";

const defaultFAQData: FAQSectionProps = {
    title: "Frequently asked questions",
    faqItems: [
        {
            question: "How secure is Gallabox’s conversational AI platform?",
            answer: (
                <div>
                    <p>
                        Gallabox is built from the ground up with security, data integrity, and customer confidentiality in mind. With advanced Role-based access control and
                        multi-factor authentication in place, you can rest assured your user data is in capable hands. Gallabox’s conversational AI chat platform is also SOC2 and
                        GDPR compliant, and supports AES-256 encryption for user data at rest and TLS 1.3 for data in transit.
                    </p>
                </div>
            ),
        },
        {
            question: "How can I get started with Gallabox?",
            answer: (
                <div>
                    Getting started with Gallabox is relatively simple. All you need is a Google Workspace account and a WhatsApp Business number to start a 7-day free trial. Once
                    you set up your WhatsApp Business API and verify your number, you’re all set to start building and automating your chat flows.
                    <a href="https://app.gallabox.com/signup" className="text-blue-500">
                        Click here to start your free trial now.
                    </a>
                </div>
            ),
        },
        {
            question: "Can Gallabox be used for e-commerce businesses?",
            answer: (
                <p>
                    Yes, Gallabox has many WhatsApp automation functionalities to scale e-commerce businesses. From WhatsApp Shop that lets you build a catalog to WhatsApp Payments
                    to request and receive payments, Gallabox has something for every e-commerce business. With power features like Broadcast, AI Chatbots, Shopify integrations,
                    and in-built e-commerce message templates, Gallabox is a no-brainer if you`&apos;`re running an online store.
                </p>
            ),
        },
        {
            question: "Is Gallabox suitable for small businesses?",
            answer: (
                <div>
                    Yes, Gallabox is well-suited for small businesses as it acts as a common ground that provides multiple solutions, while not compromising on quality or
                    effectiveness. Since small businesses thrive on fast growth, using a tool that adapts to this speed is important.
                </div>
            ),
        },
        {
            question: "Is your Conversational AI platform suitable for enterprise-level use? ",
            answer: "Yes, Conversational AI platforms are the need of the hour, especially for enterprises. Since both enterprises invest a lot, addressing both employee and customer experience is important. As Gallabox expertly handles everything to improve customer engagement, it is the best for enterprise-level use.",
        },
        {
            question: "Can I integrate Gallabox with my existing CRM?",
            answer: (
                <div>
                    Yes, Gallabox supports multiple CRM platforms like Zoho, HubSpot, LeadSquared, Odoo, Kylas, Corefactors, and LEDSAK. If you are using one of these, you can
                    easily connect it with Gallabox. Need help integrating your CRM with Gallabox? Check out this
                    <a href="https://gallabox.com/blog/whatsapp-crm-integration" className="text-blue-500">
                        link.
                    </a>
                </div>
            ),
        },
        {
            question: "Does Gallabox support multiple WhatsApp numbers?",
            answer: (
                <p>
                    Yes, Gallabox supports multiple WhatsApp numbers. You can manage multiple WhatsApp Business accounts in a single dashboard. This makes it perfect for businesses
                    with different teams or departments, as it enables easier handling of customer conversations.
                </p>
            ),
        },
        {
            question: "What types of messages can I send using Gallabox?",
            answer: (
                <p>
                    The types of messages you can send using Gallabox on WhatsApp are boundless. Here are some of the message types you can send with rich media: authentication,
                    notifications, alerts, lead qualification, lead nurture, confirmation, order updates, delivery status, support, promotions, AR/VR, and voice, to name a few.
                </p>
            ),
        },
        {
            question: "Does Gallabox support automated drip campaigns?",
            answer: (
                <p>
                    Yes, the automated WhatsApp drip campaign is one of Gallabox`&apos;`s best functionalities. You can trigger them based on user behavior, segments, engagement,
                    and CRM properties too. Gallabox also offers preset templates to help you kickstart a drip campaign faster.
                </p>
            ),
        },
        {
            question: "Is there a free trial or demo available for your platform?",
            answer: (
                <div>
                    Yes, Gallabox offers a 7-day free trial that lets you play around the platform`&apos;`s WhatsApp Business automations and explore use cases for your business.
                    You can schedule a demo from the platform once you{" "}
                    <a href="https://app.gallabox.com/signup" className="text-blue-500">
                        sign up for the trial.
                    </a>
                </div>
            ),
        },
    ],
};

const Home: React.FC = () => {
    return (
        <NewLayout
            keywords="omnichannel customer support software, whatsapp business api, whatsapp chatbot, whatsapp shared inbox, customer success management, lead management"
            title="Conversational AI Platform for WhatsApp | Gallabox"
            description="Powerful conversational AI platform for WhatsApp. Automate customer chats, simplify workflows, and grow your business with Gallabox."
            withLiveChat
            canonical="https://gallabox.com"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/new-landing/og-gallabox.png" }}
            schema={{
                heroOrOgImageAlt: "gallabox website hero-image",
                heroOrOgImageDescription: "go from  leads to deals on whatsapp",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/new-landing/og-gallabox.png",
                faq: defaultFAQData.faqItems,
            }}
        >
            <Head>
                <script src="https://fast.wistia.com/embed/medias/730lmkyzty.jsonp" async></script>
                <script src="https://fast.wistia.com/assets/external/E-v1.js" async></script>
                <link rel="alternate" hrefLang="en" href="https://gallabox.com/" />
                <link rel="alternate" hrefLang="en-us" href="https://gallabox.com/us" />
                <link rel="alternate" hrefLang="ar" href="https://gallabox.com/ar" />
                <link rel="alternate" hrefLang="x-default" href="https://gallabox.com/" />
            </Head>
            <section className="lg:pt-16 pt-10">
                <header className="lg:max-w-7xl lg:mx-auto px-5 w-full flex flex-col lg:py-8 pb-16 gap-16">
                    <div className="flex flex-col lg:flex-row lg:py-5 lg:gap-0 gap-8">
                        <div className="lg:w-1/2 w-full flex flex-col justify-center lg:gap-0 gap-4">
                            <h1 className="lg:text-6xl text-5xl font-bold lg:mb-6" style={{ fontFamily: "Degular Variable" }}>
                                The AI sales copilot for smarter conversations and higher conversions
                            </h1>
                            <p className="text-xl font-normal lg:mb-4 leading-8 lg:w-4/4">
                                Empower your sales team to scale first-touch responses, <br /> qualify and nurture every lead with context — for faster <br /> closures.
                                Purpose-built for B2C businesses.
                            </p>
                            <Image
                                width={335}
                                height={40}
                                className="mb-7"
                                src="https://assets.gallabox.com/gb-home/new-landing/hero-section/WhatsappxInstagram.png"
                                alt="WhatsappxInstagram"
                            />
                            <CTAButtons primaryButtonText="Try for free" />
                        </div>
                        <div className="lg:w-1/2 w-full flex lg:justify-end justify-center">
                            <div className="rounded-lg overflow-hidden">
                                <LazyVideo
                                    src={"https://assets.gallabox.com/gb-home/new-landing/hero-section/hero-section.mp4"}
                                    type={"video/mp4"}
                                    poster="https://assets.gallabox.com/gb-home/new-landing/hero-section/Gallabox-hero-fallback.jpg"
                                    className="rounded-lg object-contain"
                                    style={{ maxHeight: "550px" }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-full">
                        <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                        <ClientLogos />
                    </div>
                </header>
            </section>
            <ProblemStatementSection />
            <KeyFeaturesSection />
            {/* <FeaturesSection /> */}
            {/* <FAQSection faqData={defaultFAQData} /> */}
            <IntegrationSection />
            <TestimonialSection />
            <AwardsSection />
            <CTASection title="Turn connections into conversions" description="Be among the first to ride the automation wave" />
        </NewLayout>
    );
};

export default Home;
